import { ATTR_NAMES, AttrValue } from '../../types/attrNames';
import { BrandId } from '../../utils/override';
import { ProductSortDirection } from '../../utils/productSorting';
import {
  AuthoredSortOption,
  ProductSortConfig,
  ProductSortOption,
  SortOptionValue,
  SortRankBy,
} from './types';

/**
 * What the panel can do with one measure the Strapi enums offer.
 *
 * A capability registry, not a list a page renders: the list is authored, and
 * `getSortOptions` resolves it through this. A measure with no entry cannot be
 * applied, so its authored option is dropped rather than offered inert.
 */
type SortMeasure = {
  /**
   * Base of the `cms_sort` value: the measure, with no direction word in it.
   * Hand-written, because an ad URL will pin it once this ships.
   */
  slug: string;
  rankBy: SortRankBy;
  /**
   * The attribute the measure reads, typed against the relation registry. Set for
   * `ATTRIBUTE` and for `MINIMUM_INCOME`, which reads one too; `getSortOptions`
   * gates on it being present rather than on the `rankBy`.
   */
  attributeName?: AttrValue;
  /**
   * Undefined means every brand. `EIR` is SG-only by product decision (R13.3),
   * not by data: `APR` exists in all four markets.
   */
  brands?: readonly BrandId[];
};

/**
 * Keyed by the Strapi value, so an enum member added upstream without an entry
 * here is a compile error rather than an option that silently never renders.
 *
 * The two calculated PL measures read attributes rather than the calculator:
 * `RecalculateProductAttributes` has already written the current figures back,
 * so this ranks what the page shows.
 */
export const SORT_MEASURES: Record<SortOptionValue, SortMeasure> = {
  POPULAR: {
    slug: 'popular',
    rankBy: 'AUTHORED_ORDER',
  },
  EIR: {
    slug: 'eir',
    rankBy: 'ATTRIBUTE',
    attributeName: ATTR_NAMES.APR,
    brands: ['singsaver'],
  },
  ANNUAL_RATE: {
    slug: 'annual-interest-rate',
    rankBy: 'ATTRIBUTE',
    attributeName: ATTR_NAMES.ANNUAL_RATE,
  },
  TOTAL_REPAYMENT: {
    slug: 'total-repayment',
    rankBy: 'ATTRIBUTE',
    attributeName: ATTR_NAMES.TOTAL_REPAYMENT,
  },
  MONTHLY_REPAYMENT: {
    slug: 'monthly-repayment',
    rankBy: 'ATTRIBUTE',
    attributeName: ATTR_NAMES.MONTHLY_REPAYMENT,
  },
  LOAN_TENURE: {
    slug: 'loan-tenure',
    rankBy: 'ATTRIBUTE',
    attributeName: ATTR_NAMES.MINIMUM_LOAN_TENURE,
  },
  RECOMMENDED: {
    slug: 'recommended',
    rankBy: 'AUTHORED_ORDER',
  },
  PRICE: {
    slug: 'price',
    rankBy: 'PREMIUM',
  },
  DISCOUNT: {
    slug: 'discount',
    rankBy: 'DISCOUNT',
  },
  OVERSEAS_MEDICAL_EXPENSES: {
    slug: 'overseas-medical-expenses',
    rankBy: 'ATTRIBUTE',
    attributeName: ATTR_NAMES.OVERSEAS_MEDICAL_EXPENSES,
  },
  TRAVEL_CANCELLATION: {
    slug: 'travel-cancellation',
    rankBy: 'ATTRIBUTE',
    attributeName: ATTR_NAMES.TRAVEL_CANCELLATION,
  },
  LOSS_OF_BAGGAGE_OR_PERSONAL_BELONGING: {
    slug: 'loss-of-baggage-or-personal-belonging',
    rankBy: 'ATTRIBUTE',
    attributeName: ATTR_NAMES.LOSS_OF_BAGGAGE_OR_PERSONAL_BELONGING,
  },
  // The trending signal is applied upstream, by the ranking that decides the
  // order the catalogue arrives in — the same arrangement `POPULAR` and
  // `RECOMMENDED` have. Ranking it here would compete with that.
  TRENDING: {
    slug: 'trending',
    rankBy: 'AUTHORED_ORDER',
  },
  ANNUAL_FEE: {
    slug: 'annual-fee',
    rankBy: 'ATTRIBUTE',
    attributeName: ATTR_NAMES.ANNUAL_FEE,
  },
  // Not a plain `ATTRIBUTE`: an empty value means the card states no income
  // requirement, which sorts first rather than last (CMS-2002 FR-3.2.7).
  MINIMUM_ANNUAL_INCOME: {
    slug: 'minimum-annual-income',
    rankBy: 'MINIMUM_INCOME',
    attributeName: ATTR_NAMES.MINIMUM_ANNUAL_INCOME,
  },
  GIFT_VALUE: {
    slug: 'gift-value',
    rankBy: 'GIFT_VALUE',
  },
};

/** The measure a Strapi value names, or undefined when nothing can apply it. */
export const findSortMeasure = (
  value: SortOptionValue | null | undefined,
): SortMeasure | undefined => (value ? SORT_MEASURES[value] : undefined);

/**
 * `cms_sort` names exactly one option, so a descending option is suffixed and
 * the bare slug is the ascending one (R10.2.4).
 *
 * The slug names the measure and never a direction, so no value can contradict
 * the way it sorts — `discount` ascending and `discount-desc` descending, where
 * a `highest-discount` base would have read backwards on one of the two.
 */
const resolveSlug = (
  measure: SortMeasure,
  direction: ProductSortDirection,
): string => (direction === 'desc' ? `${measure.slug}-desc` : measure.slug);

/**
 * One authored option resolved against the registry.
 *
 * Dropped when nothing can apply the measure, when the brand does not offer it,
 * or when it carries no label.
 *
 * The measure carries no vertical: the two Strapi components hold disjoint
 * enums, so a page can only author what its own vertical offers.
 */
const resolveAuthored = (
  authored: AuthoredSortOption,
  config: ProductSortConfig,
): ProductSortOption | undefined => {
  const measure = SORT_MEASURES[authored.value];
  if (!measure) return undefined;

  if (measure.brands && !measure.brands.includes(config.brand as BrandId)) {
    return undefined;
  }

  // Strapi defaults the field to `asc`, so this agrees with the CMS rather
  // than inventing a per-measure default of its own.
  const direction = authored.direction ?? 'asc';
  // A built-in label would have to name a direction the measure does not own —
  // "Lowest Annual Interest Rate" is wrong for the same measure descending — so
  // the label is the editor's, and cms requires one (`presence: 'filled_string'`).
  const label = authored.label?.trim();
  if (!label) return undefined;

  return {
    sourceValue: authored.value,
    value: resolveSlug(measure, direction),
    rankBy: measure.rankBy,
    attributeName: measure.attributeName,
    direction,
    label,
    helpText: authored.helpText?.trim() || undefined,
  };
};

/**
 * The authored list, in the authored order. There is no code-owned list behind
 * it: a panel with nothing authored renders no Sort By section.
 *
 * Two entries resolving to one `cms_sort` value collapse to the first, since a
 * URL cannot name both and the dropdown would paint whichever matched first.
 *
 * The measure gate is separate from the brand gate (R13.2 vs R13.3) and keys
 * off the measure naming an attribute rather than its `rankBy`: a premium, a
 * discount and a gift value are not in the catalogue's attribute bag, so gating
 * those would drop them everywhere, while credit card's minimum-income measure
 * reads an attribute under a `rankBy` of its own and has to stay gated.
 */
export const getSortOptions = (
  config: ProductSortConfig | undefined,
): ProductSortOption[] => {
  if (!config) return [];

  const seen = new Set<string>();
  const resolved = (config.options ?? []).flatMap((authored) => {
    const option = resolveAuthored(authored, config);
    if (!option || seen.has(option.value)) return [];
    seen.add(option.value);
    return [option];
  });

  const measures = config.availableMeasures;
  if (!measures) return resolved;

  return resolved.filter(
    (option) =>
      !option.attributeName || measures.includes(option.attributeName),
  );
};
